import { useMemo } from 'react'
import type { RulerExchangeSeries } from './ChartRuler'
import { calculateOIRuler } from './oiRuler'
import { formatUtc } from './chartUtils'

function number(value: number | null, digits = 4, signed = false): string {
  if (value == null || !Number.isFinite(value)) return 'N/A'
  return `${signed && value >= 0 ? '+' : ''}${value.toLocaleString(undefined, { maximumFractionDigits: digits })}`
}

/** Each exchange uses its own observations and prices within the selected time range. */
export function RulerExchangeSummary({ exchanges, startTime, endTime, interval, candleInterval }: {
  exchanges: RulerExchangeSeries[]
  startTime: number
  endTime: number
  interval: string
  candleInterval?: string
}) {
  const rows = useMemo(() => exchanges.map((exchange) => ({
    ...exchange,
    measurement: calculateOIRuler({ ...exchange, startTime, endTime, interval, candleInterval }),
  })), [exchanges, startTime, endTime, interval, candleInterval])
  const complete = rows.length > 0 && rows.every((row) => row.measurement != null)
  const deltaCoins = complete ? rows.reduce((total, row) => total + row.measurement!.deltaCoins, 0) : null
  const costBasis = complete && rows.every((row) => row.measurement?.costBasisUsdt != null)
    ? rows.reduce((total, row) => total + row.measurement!.costBasisUsdt!, 0) : null

  return <div className="space-y-3 px-3 py-3" data-testid="ruler-exchange-summary">
    <div>
      <div className="text-xs font-semibold text-slate-200">Selected exchanges · {rows.length}</div>
      <p className="mt-1 text-[10px] leading-relaxed text-slate-400">OI calculations use the first and last samples inside the selected time range on each exchange.</p>
    </div>
    {rows.length === 0 && <p className="text-slate-400">Select an OI exchange to see its change and position.</p>}
    {rows.map(({ id, label, color, measurement }) => <div key={id} data-ruler-exchange={id} className="border-t border-white/10 pt-2">
      <div className="mb-1 flex items-center gap-1.5 font-semibold uppercase tracking-wide text-slate-200">
        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />{label}
      </div>
      {measurement ? <>
        <div className="font-mono font-semibold" style={{ color: measurement.deltaCoins >= 0 ? '#2dd4bf' : '#fb7185' }}>
          {number(measurement.deltaCoins, 4, true)} coins <span className="text-[10px]">({measurement.deltaPercent == null ? 'N/A' : `${number(measurement.deltaPercent, 2, true)}%`})</span>
        </div>
        <dl className="mt-1 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 [&>dd]:text-right [&>dd]:font-mono">
          <dt className="text-slate-400">Est. average price (USDT)</dt><dd>{number(measurement.averagePrice, 8)}</dd>
          <dt className="text-slate-300">Position @ avg (USDT)</dt><dd>{number(measurement.costBasisUsdt, 2, true)}</dd>
        </dl>
        <p className="mt-1 text-[10px] text-slate-500">{measurement.sampleCount} OI samples · {formatUtc(measurement.start.timestamp * 1000, interval === '5s')} → {formatUtc(measurement.end.timestamp * 1000, interval === '5s')}</p>
        {(measurement.hasGaps || !measurement.priceCoverageComplete || measurement.staleSamples > 0) && <p className="mt-1 text-[10px] text-amber-300">
          {measurement.hasGaps ? 'OI gaps: average unavailable. ' : ''}
          {!measurement.priceCoverageComplete ? 'Some aligned prices unavailable. ' : ''}
          {measurement.staleSamples > 0 ? `${measurement.staleSamples} samples have older exchange timestamps.` : ''}
        </p>}
      </> : <p className="text-[10px] text-slate-400">No OI samples inside this range.</p>}
    </div>)}
    {rows.length > 0 && <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 border-t border-white/10 pt-2 [&>dd]:text-right [&>dd]:font-mono">
      <dt className="text-slate-400">Total OI change (coins)</dt><dd>{number(deltaCoins, 4, true)}</dd>
      <dt className="text-slate-300">Total position @ avg (USDT)</dt><dd>{number(costBasis, 2, true)}</dd>
    </dl>}
    <p className="text-[10px] leading-relaxed text-slate-500">Position is the net OI change valued at its estimated average price. Totals sum signed exchange values. OI changes do not identify buy/sell direction or actual entries.</p>
  </div>
}
